import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { fetchAuthSession as AmplifyFetchAuthSession, type AuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defineStore } from 'pinia';
import qs from 'qs';
import Axios from 'axios';

import { HUB_STAGE_INFO } from '@base-shared/utils/constants';
import { DependencyManager } from '@base-shared/utils/dependency-manager';
import { handleRequest } from '@base-shared/utils/request-handler';
import { AppError } from '@base-shared/utils/error-handler';
import { SessionStorage } from '@base-shared/utils/session-storage';
import type { useHubAuth } from '@base-shared/composables/use-hub-auth';
import { useUserApi, useSSOApi } from '@base-shared/composables/use-api';
import type { PublicRuntimeConfig } from 'nuxt/schema';

type CognitoConfig = {
  userPoolId: string;
  userPoolClientId: string;
  domain: string;
  redirectSignIn: string[];
  redirectSignOut: string[];
};

type CognitoConfigResult = { success: boolean; error?: AppError; data?: CognitoConfig };

export type CognitoProviderInfo = {
  CognitoStage: string;
  CognitoPoolID?: string;
  CognitoClientAppID?: string;
  CognitoDomain?: string;
  SSOProviderName?: string;
};

export const useCognitoAuthStore = defineStore('cognitoAuth', {
  state: () => ({
    isAuthReady: false,
    hubAuth: null as ReturnType<typeof useHubAuth> | null,
    isSigningIn: false,
    runtimeConfig: null as null | PublicRuntimeConfig,

    authSession: null as null | AuthSession
  }),
  getters: {
    isAuthenticated: (state) => !!(state.authSession?.tokens?.accessToken && state.authSession?.tokens?.idToken),
    // @ts-expect-error: AuthSession from aws-amplify has deeply nested generics that exceed
    // TypeScript's type instantiation depth limit when Pinia applies UnwrapRef to the store state.
    sessionStage: (state) => (state.authSession?.tokens?.idToken?.payload?.['custom:group_stage'] ?? null) as string | null
  },
  actions: {
    initDefaultConfig(config: PublicRuntimeConfig) {
      this.runtimeConfig = config;
    },
    setAuthReady(isReady: boolean) {
      this.isAuthReady = isReady;
    },
    setHubAuth(hubAuth: ReturnType<typeof useHubAuth> | null) {
      this.hubAuth = hubAuth;
    },
    setSigningIn(isEnabling: boolean) {
      this.isSigningIn = isEnabling;
    },
    /**
     * Cleans up the local session by removing
     * provider info and Cognito stage from local storage,
     * and clearing the session storage.
     */
    async cleanupLocalSession() {
      this.cleanUpProviderInfoAndCognitoStage();
      await SessionStorage.clear();
    },
    cleanUpProviderInfoAndCognitoStage() {
      localStorage.removeItem(HUB_STAGE_INFO);
    },
    setProviderInfoAndCognitoStage(data: CognitoProviderInfo) {
      try {
        const serialized = JSON.stringify(data);
        localStorage.setItem(HUB_STAGE_INFO, serialized);
      } catch (_error) {
        const err = new AppError('UnexpectedError', 'Failed to serialize provider info for storage');
        console.warn(err.message);
        return { success: false, error: err };
      }

      return { success: true, error: null };
    },
    getProviderInfoAndCognitoStage(): CognitoProviderInfo | null {
      const data = localStorage.getItem(HUB_STAGE_INFO);
      try {
        return data ? JSON.parse(data) : null;
      } catch (e) {
        console.warn('Failed to parse provider info from localStorage:', e);
        return null;
      }
    },
    listenToAmplifyAuthEvents() {
      Hub.listen('auth', async ({ payload }) => {
        console.log('auth#hubEvent:' + payload.event, payload);
        const onAuthEvent = DependencyManager.auth.onAuthEvent;

        switch (payload.event) {
          case 'signedOut':
            await this.cleanupLocalSession();
            if (this.hubAuth) {
              this.hubAuth.updateAuthSession(null);
            }

            if (onAuthEvent) {
              await onAuthEvent(payload.event);
            }
            break;
          case 'tokenRefresh': {
            if (this.hubAuth) {
              this.hubAuth.updateAuthSession(await this.getSession());
            }

            if (onAuthEvent) {
              await onAuthEvent(payload.event);
            }
            break;
          }
          case 'tokenRefresh_failure':
            await this.cleanupLocalSession();
            if (this.hubAuth) {
              this.hubAuth.updateAuthSession(null);
            }

            if (onAuthEvent) {
              await onAuthEvent(payload.event);
            }
            break;
          case 'signedIn': {
            console.log('auth#signedIn Session Update');
            if (this.hubAuth) {
              this.hubAuth.updateAuthSession(await this.getSession());
            }

            this.setSigningIn(true);
            if (onAuthEvent) {
              await onAuthEvent(payload.event);
            }
            this.setSigningIn(false);
            break;
          }
        }
      });
    },
    async getProviderInfo(email: string) {
      const { success, error, response } = await handleRequest(async () => {
        const idpInfo = await useUserApi().getIdpInfo(email);
        return idpInfo?.data;
      });

      return { success, error, response };
    },
    /**
     * Resolves the login stage based on the provided email.
     * It fetches the identity provider details using the email,
     * and sets the CognitoStage in the local storage.
     *
     */
    async getCognitoStageByEmail(email: string) {
      const { error, response } = await this.getProviderInfo(email);

      if (error) {
        return { success: false, error };
      }

      const idPDetails = response;

      if (!idPDetails || !idPDetails.CognitoStage) {
        return { success: false, error: new AppError('UnexpectedError', 'No identity provider details found for the provided email.') };
      }

      const { error: storageError } = this.setProviderInfoAndCognitoStage({
        CognitoStage: idPDetails.CognitoStage
      });

      if (storageError) {
        return { success: false, error: storageError };
      }

      return { success: true, error: null };
    },
    /**
     * Sets the login stage directly in the local storage.
     * This is for scenarios where the stage is known
     * and does not require fetching from an identity provider.
     *
     * Scenarios include:
     * - When user signs up with selected stage
     * - When user signs in with SSI (Google, Facebook)
     *    since we know it's using the global stage
     */
    setCognitoStageDirectly(stage: string) {
      const { error } = this.setProviderInfoAndCognitoStage({
        CognitoStage: stage
      });

      if (error) {
        return { success: false, error };
      }

      return { success: true, error: null };
    },
    /**
     * Resolves the login stage based on the provided SSO provider.
     * It fetches the SSO provider details using the provider name,
     * and sets the CognitoStage and CognitoClientAppID in the local storage.
     */
    async getCognitoStageFromSSOProvider(provider: string) {
      const { error, response } = await handleRequest(async () => {
        const result = await useSSOApi().getProvider(provider);

        return result.data;
      });

      if (error) {
        return { success: false, error };
      }

      const { error: storageError } = this.setProviderInfoAndCognitoStage({
        CognitoStage: response.CognitoStage,
        CognitoClientAppID: response.CognitoClientAppID,
        // Fallback to CognitoIdPName if SSOProviderName is not available.
        // This ensures that SSOProviderName is always set if user is using /sso/{provider}
        SSOProviderName: response.SSOProviderName ?? response.CognitoIdPName
      });

      if (storageError) {
        return { success: false, error: storageError };
      }

      return { success: true, error: null, response: response.CognitoIdPName };
    },
    /**
     * Retrieves the Cognito configuration based on the current
     * provider and stage information stored in local storage.
     * It uses the global stage as a fallback if no specific stage is set.
     */
    getCognitoConfig(): CognitoConfigResult {
      const config = this.runtimeConfig;

      if (!config) {
        return { success: false, error: new AppError('UnexpectedError', 'Cognito configuration is not initialized.') };
      }

      // Get stage info from localstorage
      const providerInfoAndCognitoStage = this.getProviderInfoAndCognitoStage();
      const selectedStage = providerInfoAndCognitoStage?.CognitoStage ?? config.globalStage;

      const pool = config?.awsCognitoUserPools?.[selectedStage];

      // Ensure that stage is present in the config
      if (!pool) {
        return { success: false, error: new AppError('UnexpectedError', `No user pool found for stage: ${selectedStage}`) };
      }

      // Override or supplement with values from provider info
      const userPoolId = providerInfoAndCognitoStage?.CognitoPoolID ?? pool.userPoolId;
      const userPoolClientId = providerInfoAndCognitoStage?.CognitoClientAppID ?? pool.clientId;
      const domainRaw = providerInfoAndCognitoStage?.CognitoDomain ?? pool.domain;

      if (!userPoolId || !userPoolClientId || !domainRaw) {
        return {
          success: false,
          error: new AppError('UnexpectedError', `Missing required Cognito config. Stage: ${selectedStage}`)
        };
      }

      const domain = domainRaw.replace('https://', '');
      const redirectSignInUrls = [config.amplifyAuthRedirectSignIn as string];
      let redirectSignOutUrls = [config.amplifyAuthRedirectSignOut as string];

      // As long as user is logged in with SSO, define redirectSignOutUrls
      if (providerInfoAndCognitoStage?.SSOProviderName) {
        redirectSignOutUrls = [(`${config.amplifyAuthRedirectSignOut}logoutok/${providerInfoAndCognitoStage.SSOProviderName}`)];
      }

      return {
        success: true,
        data: {
          userPoolId,
          userPoolClientId,
          domain,
          redirectSignIn: redirectSignInUrls,
          redirectSignOut: redirectSignOutUrls
        }
      };
    },
    /**
     * Configures the Amplify Auth module with the Cognito configuration
     * based on the current provider and stage information.
     * It sets the Cognito identity pool ID, user pool ID, user pool client ID,
     * and OAuth settings including domain, scopes, and redirect URLs.
     */
    configureAmplify(): { success: boolean; error?: AppError } {
      const { error, success, data } = this.getCognitoConfig();

      if (!success) {
        return { error, success: false };
      }

      const {
        userPoolId,
        userPoolClientId,
        domain,
        redirectSignIn,
        redirectSignOut
      } = data!;

      const authConfig: ResourcesConfig['Auth'] = {
        Cognito: {
          identityPoolId: '',
          userPoolId,
          userPoolClientId,
          loginWith: {
            email: true,
            oauth: {
              domain,
              scopes: ['aws.cognito.signin.user.admin', 'email', 'openid', 'profile'],
              redirectSignIn,
              redirectSignOut,
              responseType: 'code'
            }
          }
        }
      };

      Amplify.configure({ Auth: authConfig });

      // Call setAuthConfig to apply configuration to cognitoUserPoolsTokenProvider
      cognitoUserPoolsTokenProvider.setAuthConfig({ Cognito: authConfig.Cognito });

      // Call setKeyValueStorage after the configure function, to override the default storage and use our custom session storage
      cognitoUserPoolsTokenProvider.setKeyValueStorage(SessionStorage);
      console.log('auth#configureAmplify Amplify config', Amplify.getConfig());

      return { success: true };
    },
    /**
     * This is one of the functions to call before any amplify/auth
     * related functions are called.
     * This ensures that stage is set directly in the local storage
     * and that the Amplify Auth module is configured with the correct
     * Cognito stage.
     */
    async configureWithCognitoStage(stage: string) {
      if (!stage || stage === '') {
        throw new AppError('UnexpectedError', 'Cognito stage is not provided.');
      }

      const { error } = this.setCognitoStageDirectly(stage);

      if (error) throw error;

      const { error: amplifyError } = this.configureAmplify();

      if (amplifyError) throw amplifyError;
    },
    /**
     * This is one of the functions to call before any amplify/auth
     * related functions are called.
     * This ensures that stage and provider info has been resolved
     * using provided email. And that the Amplify Auth module
     * is configured with the correct Cognito stage and provider.
     */
    async configureWithEmail(email: string) {
      const { error } = await this.getCognitoStageByEmail(email);

      if (error) throw error;

      const { error: amplifyError } = this.configureAmplify();

      if (amplifyError) throw amplifyError;
    },
    /**
     * This is one of the functions to call before any amplify/auth
     * related functions are called.
     * This ensures that stage and provider info has been resolved
     * using provided SSO provider. And that the Amplify Auth module
     * is configured with the correct Cognito stage and provider.
     */
    async configureWithProvider(provider: string) {
      const { error, response } = await this.getCognitoStageFromSSOProvider(provider);

      if (error) throw error;

      const { error: amplifyError } = this.configureAmplify();

      if (amplifyError) throw amplifyError;

      return { success: true, error: null, response };
    },
    /**
     * This is one of the functions to call before any amplify/auth
     * related functions are called.
     * This configures amplify according to saved provider info and
     * cognito stage to correctly handle code and token exchange
     */
    async configureForCodeAndTokenExchange() {
      const providerInforAndCognitoStage = this.getProviderInfoAndCognitoStage();

      if (!providerInforAndCognitoStage) {
        throw new AppError('UnexpectedError', 'No provider info or Cognito stage found in local storage.');
      }

      if (providerInforAndCognitoStage.SSOProviderName) {
        // If SSO provider is set, configure with SSO provider
        await this.configureWithProvider(providerInforAndCognitoStage.SSOProviderName);
      } else if (providerInforAndCognitoStage.CognitoStage) {
        // If CognitoStage is set, configure with Stage
        await this.configureWithCognitoStage(providerInforAndCognitoStage.CognitoStage);
      } else {
        // If no provider/stage info, configure with default stage
        await this.configureWithCognitoStage(this.runtimeConfig?.globalStage ?? '');
      }
    },
    /**
     * Initializes the Cognito auth session.
     * Session can either be a serialized session from Hub/Auth.html
     * or it can be initialized with the provider and stage info
     * stored in local storage.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async initializeAuthSession(sessionFromHubAuth?: null | Record<string, any>) {
      let providerInfoAndCognitoStage = this.getProviderInfoAndCognitoStage();

      // NOTE: it is important to NOT put HUB/Auth session cleanup in `cleanupLocalSession`
      // because it will not allow to restore session properly.
      if (sessionFromHubAuth !== undefined) {
        // If we have a session from Hub/Auth.html, perform clean up of local session first
        await this.cleanupLocalSession();

        providerInfoAndCognitoStage = sessionFromHubAuth?.cognitoIdP ?? providerInfoAndCognitoStage ?? null;
      }

      if (providerInfoAndCognitoStage?.SSOProviderName) {
        // If SSO provider is set, configure with SSO provider
        await this.configureWithProvider(providerInfoAndCognitoStage.SSOProviderName);
      } else if (providerInfoAndCognitoStage?.CognitoStage) {
        // If CognitoStage is set, configure with Stage
        await this.configureWithCognitoStage(providerInfoAndCognitoStage.CognitoStage);
      } else {
        // If no provider/stage info, configure with default stage
        await this.configureWithCognitoStage(this.runtimeConfig?.globalStage ?? '');
      }

      if (sessionFromHubAuth) {
        const session = sessionFromHubAuth;
        if (session.tokens) {
          await cognitoUserPoolsTokenProvider.authTokenStore.storeTokens(session.tokens);
        }
      }

      /**
       * Refer to fetchAndStoreAuthSession to also update
       * the store with the latest session information.
       * This is to ensure that the store's `authSession`
       * state is hydrated and in sync with the actual
       * session state on init.
       */
      const session = await this.fetchAndStoreAuthSession();
      const isAuthenticated = session?.tokens?.accessToken && session?.tokens?.idToken;

      return !!isAuthenticated;
    },
    /**
     * Get the current Cognito auth session including tokens
     * and provider/stage info.
     * Returns a JSON string if authenticated, otherwise null.
     */
    async getSession() {
      const tokens = await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens();

      if (!tokens?.accessToken || !tokens?.idToken) {
        return null;
      }

      const providerInfoAndCognitoStage = this.getProviderInfoAndCognitoStage();
      const sessionObj = {
        tokens: {
          ...tokens,
          accessToken: tokens.accessToken.toString(),
          idToken: tokens.idToken.toString()
        },
        cognitoIdP: providerInfoAndCognitoStage
      };

      // Ensure the session is JSON-serializable and matches JSONValue
      return JSON.parse(JSON.stringify(sessionObj));
    },
    async forceRefreshToken() {
      await AmplifyFetchAuthSession({ forceRefresh: true });
    },

    /**
     * Fetches the current auth session and updates the store.
     * This is useful to ensure that the store has the latest
     * session information especially on certain events like
     * app load, router navigation/middlewares, and amplify auth events
     * like sign in, sign out, or token refresh.
     * @returns {Promise<AuthSession | null>} - Returns the current auth session if available, otherwise null.
     */
    async fetchAndStoreAuthSession() {
      this.authSession = await AmplifyFetchAuthSession();
      return this.authSession;
    },

    /**
     * Revokes the provided OAuth token using the Cognito OAuth2 revoke endpoint.
     * This is useful for logging out users and invalidating their tokens.
     * @param poolDomain - The domain of the Cognito user pool
     * @param clientId - The client ID of the Cognito app client
     * @param token - The OAuth refresh token to be revoked
     * @returns {Promise<{ success: boolean; error?: AppError; response?: any }>} - Returns an object indicating success or failure, along with any error or response data.
     */
    async postCognitoOAuthRevoke(poolDomain: string, clientId: string, token: string) {
      const { success, error, response } = await handleRequest(async () => {
        if (!poolDomain || !clientId || !token) {
          throw new AppError('InvalidParameters', 'Missing required parameters for OAuth revoke');
        }

        const result = await Axios({
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded' },
          data: qs.stringify({
            client_id: clientId,
            token
          }),
          url: `${poolDomain}/oauth2/revoke`
        });

        return result;
      });

      return { success, error, response };
    }
  }
});
